import type { Sendable } from '@/interfaces/Sendable'
import { DocumentationService } from '@/api/client'
import { type Error } from '@/types/error'
import { DateTime } from 'luxon'
import { Filesystem } from '@capacitor/filesystem'
import { FileTransfer } from '@capacitor/file-transfer'

type TVideoMeta = {
  transmitted: boolean
  transmitted_at: string
  error_log: Error[]
  // Step tracking for retry logic - allows retrying only failed steps
  steps: {
    metadata_sent: boolean // Step 1: Video metadata sent to server
    upload_completed: boolean // Step 2: Video file uploaded to S3
    upload_completion_signaled: boolean // Step 3: Backend notified of upload completion
    file_cleaned_up: boolean // Step 4: Local video file deleted
  }
  // Store upload URL and video ID from metadata step
  upload_url?: string
  project_video_id?: number
}

export type TDehydratedVideo = {
  meta: TVideoMeta
  project_id: number
  employee_id: number
  content_type: string
  filename: string
  task_list?: string[]
  transcript?: string | null
  video_file_uri: string
  type: 'video'
}

export class Video implements Sendable {
  readonly type = 'video'
  meta: TVideoMeta
  project_id: number
  employee_id: number
  content_type: string
  filename: string
  task_list?: string[]
  transcript?: string
  video_file_uri: string
  private progressCallback?: (progress: number) => void
  private static progressListenerSetup = false

  constructor(
    project_id: number,
    employee_id: number,
    video_file_uri: string,
    task_list?: string[],
    transcript?: string,
    meta?: TVideoMeta,
    progressCallback?: (progress: number) => void,
  ) {
    this.project_id = project_id
    this.employee_id = employee_id
    this.video_file_uri = video_file_uri
    this.task_list = task_list
    this.transcript = transcript
    this.progressCallback = progressCallback

    const timestamp = new Date().getTime()
    this.content_type = 'video/mp4'
    this.filename = `video_${project_id}_${timestamp}.mp4`

    if (meta) {
      this.meta = meta
    } else {
      this.meta = {
        transmitted: false,
        transmitted_at: DateTime.now().toISO(),
        error_log: [],
        steps: {
          metadata_sent: false,
          upload_completed: false,
          upload_completion_signaled: false,
          file_cleaned_up: false,
        },
      }
    }

    // Set up global progress listener if not already done
    void this.setupGlobalProgressListener()
  }

  private static activeUploads = new Map<string, (progress: number) => void>()

  private async setupGlobalProgressListener() {
    if (!Video.progressListenerSetup) {
      Video.progressListenerSetup = true

      await FileTransfer.addListener('progress', (progressEvent) => {
        console.log('Global progress event:', progressEvent)

        // Match by URL - progressEvent.url should contain the upload URL
        const callback = Video.activeUploads.get(progressEvent.url)
        if (callback && progressEvent.type === 'upload') {
          let progress = 10 // Minimum progress

          if (
            progressEvent.lengthComputable &&
            progressEvent.contentLength > 0
          ) {
            // Calculate actual progress based on bytes uploaded
            const percentage =
              (progressEvent.bytes / progressEvent.contentLength) * 100
            progress = Math.min(Math.round(percentage * 0.85) + 10, 95) // Scale to 10-95%
          } else {
            // Fallback to time-based estimation if content length is not available
            const now = Date.now()
            const startTime =
              Video.uploadStartTimes.get(progressEvent.url) || now
            const elapsed = now - startTime
            progress = Math.min(Math.round((elapsed / 30000) * 85) + 10, 90) // 30s estimated upload
          }

          callback(progress)
        }
      })
    }
  }

  private static uploadStartTimes = new Map<string, number>()

  async send(): Promise<{
    error: false | Error
  }> {
    try {
      // Step 1: Send metadata (skip if already completed)
      if (!this.meta.steps.metadata_sent) {
        console.log('Sending video metadata:', this.filename)

        if (this.progressCallback) {
          this.progressCallback(5) // Starting metadata
        }

        const metaResult = await DocumentationService.storeProjectVideo({
          project_id: this.project_id,
          employee_id: this.employee_id,
          content_type: this.content_type,
          filename: this.filename,
          task_list: this.task_list,
          transcript: this.transcript || null,
        })

        if (!metaResult.data?.upload_url) {
          const error = {
            error_message: 'No upload URL returned from video metadata service',
            error_code: 'VIDEO_METADATA_NO_UPLOAD_URL',
          }
          this.meta.error_log.push(error)
          return { error }
        }

        // Mark metadata step as completed and store upload URL and video ID
        this.meta.steps.metadata_sent = true
        this.meta.upload_url = metaResult.data.upload_url
        this.meta.project_video_id = metaResult.data.project_video_id

        if (this.progressCallback) {
          this.progressCallback(15) // Metadata completed
        }
      }

      // Step 2: Upload file (skip if already completed)
      if (!this.meta.steps.upload_completed && this.meta.upload_url) {
        console.log('Uploading video file:', this.filename)

        if (this.progressCallback) {
          this.progressCallback(20) // Starting upload
          // Register this upload's progress callback
          Video.activeUploads.set(this.meta.upload_url, this.progressCallback)
          Video.uploadStartTimes.set(this.meta.upload_url, Date.now())
        }

        // Check if file still exists before uploading
        try {
          await Filesystem.stat({
            path: this.video_file_uri,
          })
        } catch (_error) {
          const fileNotFoundError = {
            error_message: `Video file no longer exists: ${this.video_file_uri}`,
            error_code: 'VIDEO_FILE_NOT_FOUND',
          }
          this.meta.error_log.push(fileNotFoundError)
          return { error: fileNotFoundError }
        }

        const result = await FileTransfer.uploadFile({
          url: this.meta.upload_url,
          path: this.video_file_uri,
          method: 'PUT',
          headers: {
            'Content-Type': 'video/mp4',
          },
          progress: true, // Enable progress tracking
        })

        // Clean up tracking for this upload
        Video.activeUploads.delete(this.meta.upload_url)
        Video.uploadStartTimes.delete(this.meta.upload_url)

        console.log('Upload result:', result)

        if (
          result.responseCode &&
          parseInt(result.responseCode) >= 200 &&
          parseInt(result.responseCode) < 300
        ) {
          // Mark upload step as completed
          this.meta.steps.upload_completed = true

          if (this.progressCallback) {
            this.progressCallback(95) // Upload completed
          }
        } else {
          const errorMessage = `Upload failed with response code: ${result.responseCode}`
          const error = {
            error_message: errorMessage,
            error_code: 'VIDEO_UPLOAD_FAILED',
          }
          this.meta.error_log.push(error)
          return { error }
        }
      }

      // Step 3: Signal upload completion to backend (skip if already completed)
      if (
        !this.meta.steps.upload_completion_signaled &&
        this.meta.steps.upload_completed
      ) {
        console.log('Signaling upload completion to backend:', this.filename)

        if (this.progressCallback) {
          this.progressCallback(97) // Starting completion signal
        }

        // TODO: Add DocumentationService.uploadCompleted() call when available
        if (this.meta.project_video_id) {
          const completionResult = await DocumentationService.uploadCompleted(
            this.meta.project_video_id,
          )
          console.log('Upload completion signaled:', completionResult)
        } else {
          console.warn(
            'No project video ID available for upload completion signal',
          )
        }

        // Mark completion signal step as completed
        this.meta.steps.upload_completion_signaled = true

        if (this.progressCallback) {
          this.progressCallback(98) // Completion signal sent
        }
      }

      // Step 4: Clean up file (skip if already completed)
      if (
        !this.meta.steps.file_cleaned_up &&
        this.meta.steps.upload_completion_signaled
      ) {
        await this.deleteVideoFile()
        this.meta.steps.file_cleaned_up = true

        if (this.progressCallback) {
          this.progressCallback(100) // All steps completed
        }
      }

      // Mark as fully transmitted only when all steps are completed
      if (
        this.meta.steps.metadata_sent &&
        this.meta.steps.upload_completed &&
        this.meta.steps.upload_completion_signaled &&
        this.meta.steps.file_cleaned_up
      ) {
        this.meta.transmitted = true
        this.meta.transmitted_at = DateTime.now().toISO()
      }

      return { error: false }
    } catch (error) {
      console.error('Video processing error:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Video processing failed'
      const errorObj = {
        error_message: errorMessage,
        error_code: 'VIDEO_PROCESSING_FAILED',
      }
      this.meta.error_log.push(errorObj)

      // Clean up tracking on error
      if (this.meta.upload_url) {
        Video.activeUploads.delete(this.meta.upload_url)
        Video.uploadStartTimes.delete(this.meta.upload_url)
      }

      return { error: errorObj }
    }
  }

  private async deleteVideoFile() {
    if (this.video_file_uri) {
      try {
        await Filesystem.deleteFile({
          path: this.video_file_uri,
        })
        console.log('Video file deleted successfully')
      } catch (error) {
        console.error('Failed to delete video file:', error)
      }
    }
  }

  cleanup() {
    // Only cleanup if all steps are completed
    if (this.meta.steps.file_cleaned_up) {
      // File already cleaned up in send() method
      return true
    }
    // If upload completion was signaled but cleanup wasn't done yet, do it now
    if (this.meta.steps.upload_completion_signaled) {
      void this.deleteVideoFile()
      this.meta.steps.file_cleaned_up = true
    }
    return true
  }

  isReady() {
    // Video is ready to send as soon as it's created
    return true
  }

  static fromJSON(dehydratedVideo: TDehydratedVideo) {
    return new Video(
      dehydratedVideo.project_id,
      dehydratedVideo.employee_id,
      dehydratedVideo.video_file_uri,
      dehydratedVideo.task_list,
      dehydratedVideo.transcript || undefined,
      dehydratedVideo.meta,
    )
  }

  toJSON(): TDehydratedVideo {
    return {
      type: this.type,
      meta: this.meta,
      project_id: this.project_id,
      employee_id: this.employee_id,
      content_type: this.content_type,
      filename: this.filename,
      task_list: this.task_list,
      transcript: this.transcript || null,
      video_file_uri: this.video_file_uri,
    }
  }
}
